import { rpx } from 'client/lib/rpx-client';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { LoadingIndicator } from '@components/loading-indicator';
import { showError } from '@components/app-error';
import { MemberDetailPanel } from './member-detail-panel';
import { OutreachActionDrawer } from './outreach-action-drawer';
import { AipMemberRow } from './aip-member-row';
import { SegmentDrawer } from './segment-drawer';
import { ChampionActivity } from './champion-activity';
import { bustCache, cachedFetch } from './dashboard-cache';

const MEMBER_SIGNALS_TTL_MS = 60 * 1000;

type Course = { id: UUID; title: string };
type SubView = 'segments' | 'champions';

type Member = Awaited<
  ReturnType<typeof rpx.engagementRetention.getMemberSignals>
>['members'][number];

type ExtendedSegment =
  | 'newAndFindingFeet'
  | 'cameOnceAndDrifted'
  | 'slippingAway'
  | 'asyncEngaged'
  | 'champions';

const SEGMENT_ORDER: ExtendedSegment[] = [
  'newAndFindingFeet',
  'cameOnceAndDrifted',
  'slippingAway',
  'asyncEngaged',
  'champions',
];

const SEGMENT_LABELS: Record<
  ExtendedSegment,
  { title: string; subtitle: string; emptyText: string; action: string }
> = {
  newAndFindingFeet: {
    title: 'Finding their feet',
    subtitle: 'Joined 2-8 weeks ago, no call yet',
    emptyText: 'Every new member has been to a call. Nice.',
    action: 'Send personal invite',
  },
  cameOnceAndDrifted: {
    title: 'Came once, drifted',
    subtitle: '1 call attended, none in 28+ days',
    emptyText: 'No drifters right now.',
    action: 'Send check-in',
  },
  slippingAway: {
    title: 'Slipping away',
    subtitle: '2+ prior calls, none in 2+ weeks',
    emptyText: 'Nobody is slipping.',
    action: 'Send back-in nudge',
  },
  asyncEngaged: {
    title: 'Async engaged',
    subtitle: 'Open emails but never come to calls',
    emptyText: 'No async-only members detected.',
    action: 'Acknowledge',
  },
  champions: {
    title: 'Champions',
    subtitle: 'Attended 4+ recent calls',
    emptyText: 'No standout regulars yet.',
    action: 'Send shoutout',
  },
};

export function AipMembersTab(props: { courses: Course[] }) {
  const [view, setView] = useState<SubView>('segments');
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<
    Awaited<ReturnType<typeof rpx.engagementRetention.getMemberSignals>> | undefined
  >(undefined);
  const [activeMember, setActiveMember] = useState<Member | undefined>(undefined);
  const [foundMemberId, setFoundMemberId] = useState<UUID | undefined>(undefined);
  const [drawer, setDrawer] = useState<
    { member: Member; kind: 'note' | 'outcome' | 'invite' | 'shoutout' } | undefined
  >(undefined);
  const [activeSegment, setActiveSegment] = useState<ExtendedSegment | undefined>(undefined);
  const [cancelDrawerOpen, setCancelDrawerOpen] = useState(false);

  const [syncResult, setSyncResult] = useState<string | undefined>(undefined);
  const [isSyncing, setIsSyncing] = useState(false);

  async function refresh(opts?: { force?: boolean }) {
    setIsLoading(true);
    try {
      if (opts?.force) {
        bustCache('memberSignals');
      }
      const result = await cachedFetch(
        'memberSignals',
        () => rpx.engagementRetention.getMemberSignals({}),
        MEMBER_SIGNALS_TTL_MS,
      );
      setData(result);
    } catch (err) {
      showError(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function syncOp() {
    setIsSyncing(true);
    setSyncResult(undefined);
    try {
      const result = await rpx.engagementRetention.syncOpMembers({ limit: 50 });
      setSyncResult(
        `Synced ${result.attempted} members (${result.succeeded} succeeded, ${result.failed} failed). Refresh to see updates.`,
      );
      bustCache('memberSignals');
    } catch (err) {
      showError(err);
    } finally {
      setIsSyncing(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const pulse = useMemo(() => {
    if (!data) {
      return undefined;
    }
    const active = data.members.filter((m) => m.isEnabled);
    const total = active.length;
    const withAnyCall = active.filter((m) => m.callsAttended > 0).length;
    const newThisMonth = active.filter((m) => m.tenureDays <= 30).length;
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - thirtyDaysMs;
    const isRecent = (d?: Date | string) => {
      if (!d) {
        return false;
      }
      return new Date(d).getTime() >= cutoff;
    };
    let atRisk = 0;
    let engaged = 0;
    let lapsed = 0;
    for (const m of active) {
      if (m.cancellationStatus === 'canceling') {
        atRisk += 1;
        continue;
      }
      const recentActivity =
        isRecent(m.lastCallAttendedAt) ||
        isRecent(m.lastPostAt) ||
        m.emailEngagementBucket === 'active_live';
      if (recentActivity) {
        engaged += 1;
      } else {
        lapsed += 1;
      }
    }
    const cancelling = data.members.filter((m) =>
      isRecentlyCancellingOrCancelled(m, cutoff),
    ).length;
    return { total, engaged, lapsed, atRisk, withAnyCall, newThisMonth, cancelling };
  }, [data]);

  const cancellingMembers = useMemo(() => {
    if (!data) {
      return [];
    }
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - thirtyDaysMs;
    return data.members.filter((m) => isRecentlyCancellingOrCancelled(m, cutoff));
  }, [data]);

  if (isLoading || !data || !pulse) {
    return <LoadingIndicator />;
  }

  return (
    <div class="flex flex-col gap-6">
      <PulseStrip
        pulse={pulse}
        onCancelledClick={
          cancellingMembers.length > 0 ? () => setCancelDrawerOpen(true) : undefined
        }
      />

      <FindMember onOpen={setFoundMemberId} />

      <div class="flex gap-2 border-b border-gray-200">
        <SubViewBtn
          label="Segments"
          active={view === 'segments'}
          onClick={() => setView('segments')}
        />
        <SubViewBtn
          label="Champions"
          active={view === 'champions'}
          onClick={() => setView('champions')}
        />
      </div>

      {view === 'champions' ? (
        <ChampionActivity courses={props.courses} />
      ) : (
        <div class="rounded-xl border border-gray-200 bg-white p-5">
          <div class="mb-4 flex items-baseline justify-between">
            <div>
              <h3 class="text-base font-semibold text-gray-900">Who needs you this week</h3>
              <p class="text-xs text-gray-500">
                Click any member for the full picture. Use the action button to log outreach.
              </p>
            </div>
            <div class="flex items-center gap-2">
              {syncResult && <span class="text-xs text-gray-600">{syncResult}</span>}
              <button
                type="button"
                class="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                onClick={syncOp}
                disabled={isSyncing}
              >
                {isSyncing ? 'Syncing OP…' : 'Sync OP (50)'}
              </button>
              <button
                type="button"
                class="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => refresh({ force: true })}
              >
                Refresh
              </button>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {SEGMENT_ORDER.map((seg) => (
              <SegmentPanel
                key={seg}
                label={SEGMENT_LABELS[seg]}
                members={data.segments[seg]}
                onOpen={(m) => setActiveMember(m)}
                onAction={(m) => setDrawer({ member: m, kind: kindForSegment(seg) })}
                onViewAll={() => setActiveSegment(seg)}
              />
            ))}
          </div>
        </div>
      )}

      {activeSegment && (
        <SegmentDrawer
          title={SEGMENT_LABELS[activeSegment].title}
          subtitle={SEGMENT_LABELS[activeSegment].subtitle}
          members={data.segments[activeSegment]}
          actionLabel={SEGMENT_LABELS[activeSegment].action}
          emptyText={SEGMENT_LABELS[activeSegment].emptyText}
          onClose={() => setActiveSegment(undefined)}
          onOpen={(m) => setActiveMember(m)}
          onAction={(m) => setDrawer({ member: m, kind: kindForSegment(activeSegment) })}
        />
      )}

      {cancelDrawerOpen && (
        <SegmentDrawer
          title="Canceling and recently cancelled"
          subtitle="Mid-cancellation now, plus anyone whose access ended in the last 30 days."
          members={cancellingMembers}
          actionLabel="Log outreach"
          emptyText="No cancellations in the last 30 days."
          onClose={() => setCancelDrawerOpen(false)}
          onOpen={(m) => setActiveMember(m)}
          onAction={(m) => setDrawer({ member: m, kind: 'note' })}
        />
      )}

      {drawer && (
        <OutreachActionDrawer
          member={drawer.member}
          source="barb_segment"
          kind={drawer.kind === 'invite' ? 'note' : drawer.kind}
          onClose={() => setDrawer(undefined)}
          onLogged={(opts) => {
            if (!opts?.keepOpen) {
              setDrawer(undefined);
            }
            bustCache('memberSignals');
          }}
        />
      )}

      {foundMemberId && (
        <MemberDetailPanel
          memberUserId={foundMemberId}
          membershipScope="all"
          onClose={() => setFoundMemberId(undefined)}
          onDataMutated={() => refresh({ force: true })}
        />
      )}

      {activeMember && (
        <MemberDetailPanel
          memberUserId={activeMember.userId}
          onClose={() => setActiveMember(undefined)}
          onDataMutated={() => refresh({ force: true })}
        />
      )}
    </div>
  );
}

const FIND_MEMBER_LIMIT = 10;

type FoundMember = Awaited<ReturnType<typeof rpx.engagementRetention.findAipMembers>>[number];

/**
 * Look anyone up by name or email and open their panel. The segment lists only
 * show who needs outreach, so there was no way to reach a specific member's
 * panel (to check their intake answers, say) unless they happened to be in one
 * (Lizzie, 2026-10-08). Searches on the server so staff and test accounts,
 * which the lists leave out, can be found too, flagged as such.
 */
function FindMember(props: { onOpen: (userId: UUID) => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FoundMember[] | undefined>(undefined);
  const term = query.trim();

  useEffect(() => {
    if (term.length < 2) {
      setResults(undefined);
      return;
    }
    let isCurrent = true;
    const timer = setTimeout(() => {
      rpx.engagementRetention
        .findAipMembers({ query: term })
        .then((found) => {
          if (isCurrent) {
            setResults(found);
          }
        })
        .catch(showError);
    }, 250);
    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [term]);

  return (
    <div class="rounded-xl border border-gray-200 bg-white p-5 flex flex-col gap-3">
      <label class="flex flex-col gap-1">
        <span class="text-sm font-semibold text-gray-900">Find a member</span>
        <input
          type="search"
          class="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm"
          placeholder="Search by name or email…"
          value={query}
          onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
        />
      </label>
      {term.length >= 2 && results && (
        <div class="flex flex-col">
          {results.length === 0 && (
            <p class="text-sm text-gray-500">No AI Playground member matches “{term}”.</p>
          )}
          {results.slice(0, FIND_MEMBER_LIMIT).map((m) => (
            <button
              key={m.userId}
              type="button"
              class="flex items-baseline justify-between gap-3 border-t border-gray-100 py-2 text-left first:border-t-0 hover:bg-gray-50"
              onClick={() => props.onOpen(m.userId)}
            >
              <span class="min-w-0 truncate text-sm text-indigo-700">
                {m.name || m.email}
                {m.name && <span class="ml-2 text-gray-500">{m.email}</span>}
              </span>
              <span class="flex shrink-0 items-center gap-2 text-xs text-gray-500">
                {m.isStaffOrTest && (
                  <span class="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                    Staff / test
                  </span>
                )}
                {m.isEnabled ? 'Active' : 'Access ended'}
              </span>
            </button>
          ))}
          {results.length > FIND_MEMBER_LIMIT && (
            <p class="pt-2 text-xs text-gray-500">More match — keep typing to narrow it down.</p>
          )}
        </div>
      )}
    </div>
  );
}

function SubViewBtn(props: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      class={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        props.active
          ? 'border-indigo-600 text-indigo-700'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {props.label}
    </button>
  );
}

function PulseStrip(props: {
  pulse: {
    total: number;
    engaged: number;
    lapsed: number;
    atRisk: number;
    withAnyCall: number;
    newThisMonth: number;
    cancelling: number;
  };
  onCancelledClick?: () => void;
}) {
  const { total, engaged, lapsed, atRisk, withAnyCall, newThisMonth, cancelling } = props.pulse;
  const safeTotal = total || 1;
  const engagedPct = (engaged / safeTotal) * 100;
  const lapsedPct = (lapsed / safeTotal) * 100;
  const atRiskPct = (atRisk / safeTotal) * 100;
  const everCalledPct = total ? Math.round((withAnyCall / total) * 100) : 0;
  const net = newThisMonth - cancelling;
  const flowDenom = Math.max(newThisMonth + cancelling, 1);
  const joinedFlowPct = (newThisMonth / flowDenom) * 100;
  const cancelledFlowPct = (cancelling / flowDenom) * 100;

  return (
    <div class="rounded-xl border border-gray-200 bg-white p-5">
      <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">
        AI Playground community
      </div>
      <div class="mt-1 flex items-baseline gap-2">
        <span class="text-3xl font-semibold text-gray-900">{total.toLocaleString()}</span>
        <span class="text-sm text-gray-600">active members</span>
      </div>

      <div class="mt-4">
        <div class="flex h-2.5 overflow-hidden rounded-full bg-gray-100">
          <div class="bg-emerald-500" style={`width:${engagedPct}%`} title={`${engaged} engaged`} />
          <div class="bg-slate-300" style={`width:${lapsedPct}%`} title={`${lapsed} lapsed`} />
          {atRisk > 0 && (
            <div class="bg-amber-500" style={`width:${atRiskPct}%`} title={`${atRisk} at risk`} />
          )}
        </div>
        <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
          <CompositionLegend
            dotClass="bg-emerald-500"
            label="Engaged"
            count={engaged}
            pct={engagedPct}
          />
          <CompositionLegend
            dotClass="bg-slate-300"
            label="Lapsed"
            count={lapsed}
            pct={lapsedPct}
          />
          {atRisk > 0 && (
            <CompositionLegend
              dotClass="bg-amber-500"
              label="At risk"
              count={atRisk}
              pct={atRiskPct}
            />
          )}
        </div>
        <div class="mt-1 text-[11px] text-gray-500">
          Engaged = call, post, or email click in last 30 days. At risk = canceling.
        </div>
      </div>

      <div class="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ActivationTile attended={withAnyCall} total={total} pct={everCalledPct} />
        <FlowTile
          joined={newThisMonth}
          cancelled={cancelling}
          net={net}
          joinedFlowPct={joinedFlowPct}
          cancelledFlowPct={cancelledFlowPct}
          onCancelledClick={props.onCancelledClick}
        />
      </div>
    </div>
  );
}

function CompositionLegend(props: { dotClass: string; label: string; count: number; pct: number }) {
  return (
    <span class="flex items-center gap-1.5">
      <span class={`inline-block h-2 w-2 rounded-full ${props.dotClass}`} />
      <span class="font-medium text-gray-800">{props.count.toLocaleString()}</span>
      <span class="text-gray-600">{props.label}</span>
      <span class="text-gray-400">({Math.round(props.pct)}%)</span>
    </span>
  );
}

function ActivationTile(props: { attended: number; total: number; pct: number }) {
  return (
    <div class="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div class="flex items-baseline justify-between">
        <span class="text-xs font-medium text-gray-600">Attended a call</span>
        <span class="text-xs text-gray-500">
          {props.attended.toLocaleString()} of {props.total.toLocaleString()}
        </span>
      </div>
      <div class="mt-1 text-2xl font-semibold text-gray-900">{props.pct}%</div>
      <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200">
        <div class="h-full bg-emerald-500" style={`width:${props.pct}%`} />
      </div>
    </div>
  );
}

function FlowTile(props: {
  joined: number;
  cancelled: number;
  net: number;
  joinedFlowPct: number;
  cancelledFlowPct: number;
  onCancelledClick?: () => void;
}) {
  const netSign = props.net > 0 ? '+' : props.net < 0 ? '' : '';
  const netColor =
    props.net > 0 ? 'text-emerald-700' : props.net < 0 ? 'text-amber-700' : 'text-gray-700';
  const cancelledIsClickable = Boolean(props.onCancelledClick) && props.cancelled > 0;
  return (
    <div class="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div class="flex items-baseline justify-between">
        <span class="text-xs font-medium text-gray-600">Last 30 days</span>
        <span class={`text-xs font-medium ${netColor}`}>
          net {netSign}
          {props.net.toLocaleString()}
        </span>
      </div>
      <div class="mt-1 flex items-baseline gap-3">
        <span class="flex items-baseline gap-1">
          <span class="text-emerald-600">↑</span>
          <span class="text-base font-semibold text-gray-900">{props.joined.toLocaleString()}</span>
          <span class="text-xs text-gray-500">joined</span>
        </span>
        {cancelledIsClickable ? (
          <button
            type="button"
            class="flex items-baseline gap-1 rounded -mx-1 px-1 hover:bg-amber-50"
            onClick={props.onCancelledClick}
            title="View members who cancelled"
          >
            <span class="text-amber-700">↓</span>
            <span class="text-base font-semibold text-amber-800 underline decoration-amber-300 decoration-dotted underline-offset-2">
              {props.cancelled.toLocaleString()}
            </span>
            <span class="text-xs text-gray-500">cancelled</span>
          </button>
        ) : (
          <span class="flex items-baseline gap-1">
            <span class="text-amber-700">↓</span>
            <span class="text-base font-semibold text-gray-900">
              {props.cancelled.toLocaleString()}
            </span>
            <span class="text-xs text-gray-500">cancelled</span>
          </span>
        )}
      </div>
      <div class="mt-2 flex h-1.5 overflow-hidden rounded-full bg-gray-200">
        <div class="bg-emerald-500" style={`width:${props.joinedFlowPct}%`} />
        <div class="bg-amber-400" style={`width:${props.cancelledFlowPct}%`} />
      </div>
    </div>
  );
}

function isRecentlyCancellingOrCancelled(m: Member, cutoffMs: number): boolean {
  if (m.cancellationStatus === 'canceling') {
    return true;
  }
  if (m.cancellationStatus === 'canceled') {
    return true;
  }
  if (!m.isEnabled && m.membershipCancelledAt) {
    return new Date(m.membershipCancelledAt).getTime() >= cutoffMs;
  }
  return false;
}

function kindForSegment(seg: ExtendedSegment): 'note' | 'invite' | 'shoutout' {
  if (seg === 'asyncEngaged') {
    return 'note';
  }
  if (seg === 'champions') {
    return 'shoutout';
  }
  return 'invite';
}

function SegmentPanel(props: {
  label: { title: string; subtitle: string; emptyText: string; action: string };
  members: Member[];
  onOpen: (m: Member) => void;
  onAction: (m: Member) => void;
  onViewAll: () => void;
}) {
  return (
    <section class="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <header class="mb-3 flex items-baseline justify-between">
        <div>
          <h4 class="text-sm font-semibold text-gray-900">
            {props.label.title}{' '}
            <span class="text-xs font-normal text-gray-500">({props.members.length})</span>
          </h4>
          <p class="text-xs text-gray-500">{props.label.subtitle}</p>
        </div>
        {props.members.length > 0 && (
          <button
            type="button"
            class="shrink-0 text-xs font-medium text-indigo-700 hover:underline"
            onClick={props.onViewAll}
          >
            View all →
          </button>
        )}
      </header>
      {props.members.length === 0 ? (
        <p class="text-sm text-gray-500">{props.label.emptyText}</p>
      ) : (
        <div class="flex flex-col">
          {props.members.slice(0, 8).map((m) => (
            <AipMemberRow
              key={m.userId}
              member={m}
              onOpen={props.onOpen}
              onAction={props.onAction}
              actionLabel={props.label.action}
            />
          ))}
          {props.members.length > 8 && (
            <button
              type="button"
              class="mt-2 self-start text-xs font-medium text-indigo-700 hover:underline"
              onClick={props.onViewAll}
            >
              View all {props.members.length} →
            </button>
          )}
        </div>
      )}
    </section>
  );
}
